import React, { useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  SecondaryButtonInline,
  UserBadge,
} from '../../components';
import { profilePageLinkProps, profileRedirectProps } from '../../util/profileSeoUrl';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import CompanyProfilePage from './CompanyProfilePage';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  const profileUser = ensureUser(user);
  const metadata = profileUser?.attributes?.profile?.metadata;
  const { isVerified, isTopSeller } = metadata || {};

  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <>
            <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
            <UserBadge isVerified={isVerified} isTopSeller={isTopSeller} />
          </>
        ) : null}
      </H2>
      {isCurrentUser ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;
  const subscriptionName = metadata?.subscriptionName

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} subscriptionName={subscriptionName}/>
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const {
    userShowError,
    profileUserId,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    viewport,
    publicData,
    metadata,
    userFieldConfig,
    intl,
  } = props;

  const { isVerified, isTopSeller } = metadata || {};

  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <div className={css.headingWithBudge} >
        <H2 as="h1" className={css.desktopHeading}>
          <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
        </H2>
        <UserBadge isVerified={isVerified} isTopSeller={isTopSeller} />
      </div>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}
      <CustomUserFields
        publicData={publicData}
        metadata={metadata}
        userFieldConfig={userFieldConfig}
        intl={intl}
      />
      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
            {profileUserId ? <SecondaryButtonInline
              style={{ marginLeft: '20px' }}
              type="button"
              onClick={() => history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, {
                authorId: profileUserId,
                showByUser: displayName,
              }))}
            >
              <FormattedMessage id="ProfilePage.searchListingsByUser" />
            </SecondaryButtonInline> : null}
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} showFavoriteIcons={true} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )}
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const pathParams = useParams();
  const location = useLocation();
  const { scrollingDisabled, currentUser, userShowError, user, intl, ...rest } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // Where this profile belongs: /u/:slug/:id for a company, /u/:id for a private
  // seller. Null while the user is still loading — the slug comes from the
  // display name, so until then there is nothing to compare against.
  const canonicalLink = profilePageLinkProps(profileUser);

  // Used as the `mainEntity` URL in the company profile's structured data — a
  // ProfilePage without mainEntity is rejected by Google. Only company sellers
  // carry structured data; private sellers are not indexed at all (below).
  const profileUrl = canonicalLink
    ? `${config.marketplaceRootURL}${createResourceLocatorString(
        canonicalLink.name,
        routeConfiguration,
        canonicalLink.params
      )}`
    : null;

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // Send every other address for this profile to the canonical one, so a shop
  // has a single URL that can collect its ranking. The slug is cosmetic and the
  // page keys on the UUID, so a stale or mistyped slug is a redirect, not a 404
  // — which is what makes a shop safe to rename. server/index.js turns these
  // into 301s. A private seller's name never belongs in the address, so a
  // slugged URL for one is sent back to /u/:id.
  const redirectTo = profileRedirectProps(profileUser, pathParams);
  if (redirectTo) {
    // The hash rides along: "Visa katalogen på din profil" links to #katalog.
    return <NamedRedirect {...redirectTo} search={location.search} hash={location.hash} />;
  }

  // Company ("Återförsäljare") accounts get the new "Bygg ditt hjul"
  // profile; private sellers keep the layout below unchanged.
  if (publicData?.userType === 'business') {
    return (
      <CompanyProfilePage
        scrollingDisabled={scrollingDisabled}
        schemaTitle={schemaTitle}
        profileUrl={profileUrl}
        user={user}
        displayName={displayName}
        profileUserId={profileUser?.id?.uuid}
        metadata={metadata}
        publicData={publicData}
        listings={rest.listings || []}
        catalogueListings={rest.catalogueListings || []}
        catalogueNextPage={rest.catalogueNextPage || null}
        catalogueInProgress={!!rest.catalogueInProgress}
        catalogueFilter={rest.catalogueFilter || {}}
        catalogueHasAny={!!rest.catalogueHasAny}
        reviews={rest.reviews || []}
        userShowError={userShowError}
        queryListingsError={rest.queryListingsError}
      />
    );
  }

  // Everything below this point is a private seller's page. A private profile is
  // one person's name, place and things for sale, so under GDPR it does not
  // belong in a search engine: keep it out with `noindex`. The structured data
  // goes with it — a Person/ProfilePage description of someone who is not being
  // indexed serves no purpose and only hands out the same personal data in a
  // machine-readable form. Company sellers ("Återförsäljare") are businesses and
  // stay indexable; they returned above, via CompanyProfilePage.
  return (
    <Page scrollingDisabled={scrollingDisabled} title={schemaTitle} noindex>
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        sideNav={
          <AsideContent user={user} isCurrentUser={isCurrentUser} displayName={displayName} />
        }
        footer={<FooterContainer />}
      >
        <MainContent
          profileUserId={profileUser?.id?.uuid}
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          intl={intl}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    catalogueRefs,
    catalogueNextPage,
    catalogueInProgress,
    catalogueFilter,
    catalogueHasAny,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  let listings = getMarketplaceEntities(state, userListingRefs);
  listings = listings.filter(listing => {
    if (
      listing.attributes.publicData.isHide &&
      currentUser &&
      listing.author.id.uuid === currentUser.id.uuid
    ) {
      return listing;
    }
    if (!listing.attributes.publicData.isHide) {
      return listing;
    }
  });
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    // P25009-410 — the dealer's catalogue (CompanyProfilePage only).
    catalogueListings: getMarketplaceEntities(state, catalogueRefs || []),
    catalogueNextPage,
    catalogueInProgress,
    catalogueFilter,
    catalogueHasAny,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
